import React, { useEffect, useState } from 'react';
import Appear from '../components/ui/Appear';
import Reveal from '../components/ui/Reveal';
import {
  Announcements,
  DndContext,
  DragEndEvent,
  DraggableAttributes,
  DraggableSyntheticListeners,
  KeyboardSensor,
  PointerSensor,
  TouchSensor,
  closestCenter,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  SortableContext,
  arrayMove,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import EmptyState from '../components/ui/EmptyState';
import Tooltip from '../components/ui/Tooltip';
import { sectionDomId } from './DocSection';
import type { TreeSection } from './types';

export const PROPOSED_DOM_ID = 'np-proposed';

const prefersReducedMotion = () =>
  typeof window !== 'undefined' && !!window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;

/**
 * The section being read: the first one inside a band near the top of the
 * viewport. Called once by the document view, so the rail and the mobile
 * disclosure share one observer.
 */
export function useActiveSection(ids: string[]): [string | null, (id: string) => void] {
  const [active, setActive] = useState<string | null>(ids[0] ?? null);
  const key = ids.join('|');

  useEffect(() => {
    setActive((prev) => (prev && ids.includes(prev) ? prev : ids[0] ?? null));
    // jsdom and very old browsers have no IntersectionObserver: the first section stays current.
    if (typeof IntersectionObserver === 'undefined' || ids.length === 0) return undefined;
    const prefix = sectionDomId('');
    const visible = new Set<string>();
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          const id = entry.target.id.slice(prefix.length);
          if (entry.isIntersecting) visible.add(id);
          else visible.delete(id);
        });
        const first = ids.find((id) => visible.has(id));
        if (first) setActive(first);
      },
      { rootMargin: '-15% 0px -65% 0px' },
    );
    ids.forEach((id) => {
      const el = document.getElementById(sectionDomId(id));
      if (el) observer.observe(el);
    });
    return () => observer.disconnect();
    // `key` stands in for `ids`, which is a fresh array every render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key]);

  return [active, setActive];
}

// ── Blocks ──────────────────────────────────────────────────────────────────
//
// Topics joined by arrows form a block that moves as one; a lone topic is a
// block of one. Blocks are counted per container (the root, or one group's
// children), and the server keeps a block contiguous inside its container, so
// a block is a run of one container's children sharing `group`.

export interface Block {
  /** The block's first section id: its sortable id. */
  key: string;
  ids: string[];
}

/** Pass one container's children, not the flat list: `group` restarts in every container. */
export const toBlocks = (sections: TreeSection[]): Block[] => {
  const blocks: Block[] = [];
  sections.forEach((s, i) => {
    const current = blocks[blocks.length - 1];
    if (current && i > 0 && sections[i - 1].group === s.group) current.ids.push(s.id);
    else blocks.push({ key: s.id, ids: [s.id] });
  });
  return blocks;
};

const GripIcon: React.FC = () => (
  <svg width="8" height="12" viewBox="0 0 8 12" fill="none" aria-hidden="true">
    <path
      fill="currentColor"
      d="M2 0.8a1.2 1.2 0 1 1 0 2.4 1.2 1.2 0 0 1 0-2.4zm4 0a1.2 1.2 0 1 1 0 2.4 1.2 1.2 0 0 1 0-2.4zM2 4.8a1.2 1.2 0 1 1 0 2.4 1.2 1.2 0 0 1 0-2.4zm4 0a1.2 1.2 0 1 1 0 2.4 1.2 1.2 0 0 1 0-2.4zM2 8.8a1.2 1.2 0 1 1 0 2.4 1.2 1.2 0 0 1 0-2.4zm4 0a1.2 1.2 0 1 1 0 2.4 1.2 1.2 0 0 1 0-2.4z"
    />
  </svg>
);

// A named group: hovering one block reveals its grip, not every grip in the rail.
const GRIP_REVEAL =
  'opacity-0 group-hover/block:opacity-100 group-focus-within/block:opacity-100 [@media(hover:none)]:opacity-100 transition-opacity duration-150 ease-emphasized motion-reduce:transition-none';
// Inside a linked group each row reveals its own grip.
const ITEM_GRIP_REVEAL =
  'opacity-0 group-hover/item:opacity-100 group-focus-within/item:opacity-100 [@media(hover:none)]:opacity-100 transition-opacity duration-150 ease-emphasized motion-reduce:transition-none';

const GRIP =
  'np-state flex h-8 w-6 touch-none cursor-grab items-center justify-center rounded-md text-on-surface-variant hover:text-on-surface active:cursor-grabbing focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/50';

const ITEM =
  'relative flex min-h-[32px] w-full min-w-0 items-center gap-1.5 rounded-md py-1 pl-2 pr-2 text-left text-[13px] leading-snug transition-colors duration-150 ease-emphasized motion-reduce:transition-none focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/50';

interface ItemProps {
  section: TreeSection;
  title: string;
  on: boolean;
  fresh: number;
  done: boolean;
  onJump: (id: string) => void;
}

const RailItem: React.FC<ItemProps> = ({ section, title, on, fresh, done, onJump }) => (
  // A group reads as a heading: weight instead of a number.
  <a
    href={`#${sectionDomId(section.id)}`}
    aria-current={on ? 'location' : undefined}
    onClick={(e) => {
      e.preventDefault();
      onJump(section.id);
    }}
    className={`${ITEM} ${section.kind === 'group' ? 'font-semibold' : ''} ${
      on ? 'bg-surface-container-high text-on-surface' : 'np-state text-on-surface-variant hover:text-on-surface'
    }`}
  >
    {/* A done topic has no number; its check takes the number's slot. */}
    {done ? (
      <svg aria-hidden="true" viewBox="0 0 24 24" className="h-3.5 w-3.5 flex-shrink-0 opacity-60" fill="none">
        <path fill="currentColor" d="M9 16.2 4.8 12l-1.4 1.4L9 19 21 7l-1.4-1.4z" />
      </svg>
    ) : (
      section.number && (
        <>
          <span className="flex-shrink-0 font-mono text-[11.5px] tabular-nums">{section.number}</span>{' '}
        </>
      )
    )}
    <Tooltip label={done ? `${title} (done)` : title} truncated>
      <span className={`min-w-0 truncate ${done ? 'opacity-60' : ''}`}>{title}</span>
    </Tooltip>
    {done && (
      <>
        {' '}
        <span className="sr-only">(done)</span>
      </>
    )}
    {fresh > 0 && <span className="sr-only">({fresh} new)</span>}
    <Appear show={fresh > 0} className="ml-auto flex-shrink-0">
      <Tooltip label={`${fresh} new since you last looked`}>
        <span
          aria-hidden="true"
          data-count={fresh}
          // The number is drawn by CSS from data-count so it adds no text node: the
          // sr-only span above is the one that names the count.
          className="block rounded-full bg-surface-container-highest px-1.5 text-[11px] font-semibold leading-[18px] tabular-nums text-on-surface after:content-[attr(data-count)]"
        />
      </Tooltip>
    </Appear>
  </a>
);

interface HandleProps {
  label: string;
  linked: boolean;
  activator: (el: HTMLElement | null) => void;
  attributes: DraggableAttributes;
  listeners: DraggableSyntheticListeners;
}

const sensorOptions = {
  pointer: { activationConstraint: { distance: 6 } },
  touch: { activationConstraint: { delay: 150, tolerance: 8 } },
  keyboard: { coordinateGetter: sortableKeyboardCoordinates },
};

// Drag moves the item up and down only: sideways would follow the pointer off the rail and scroll the page.
const VERTICAL_ONLY = [({ transform }: { transform: { x: number; y: number; scaleX: number; scaleY: number } }) => ({ ...transform, x: 0 })];

/** A rail link with its grip beside it, or the bare link when there is no grip. */
const Row: React.FC<{ link: React.ReactNode; grip?: React.ReactNode }> = ({ link, grip }) =>
  grip ? (
    <div className="flex items-center">
      {link}
      {grip}
    </div>
  ) : (
    <>{link}</>
  );

const BlockGrip: React.FC<{ handle: HandleProps }> = ({ handle }) => (
  <Tooltip
    label={
      handle.linked
        ? 'Drag to move these linked topics together, or press Space and use the arrow keys'
        : 'Drag to reorder, or press Space and use the arrow keys'
    }
  >
    <button
      type="button"
      ref={handle.activator}
      {...handle.attributes}
      {...handle.listeners}
      aria-label={`Reorder ${handle.label}`}
      className={`${GRIP} flex-shrink-0 ${GRIP_REVEAL}`}
    >
      <GripIcon />
    </button>
  </Tooltip>
);

interface GroupItemProps {
  id: string;
  title: string;
  link: React.ReactNode;
  /** The whole group's grip, which this row carries instead of its own. */
  blockGrip?: React.ReactNode;
  /** What this row holds when it is a group, nested under it. */
  below?: React.ReactNode;
}

const SortableGroupItem: React.FC<GroupItemProps> = ({ id, title, link, blockGrip, below }) => {
  const { attributes, listeners, setNodeRef, setActivatorNodeRef, transform, transition, isDragging } = useSortable({
    id,
    // Still a place to drop on; its grip moves the group, so it is not picked up alone.
    disabled: blockGrip ? { draggable: true } : false,
  });
  const style: React.CSSProperties = {
    transform: CSS.Translate.toString(transform),
    transition: prefersReducedMotion() ? undefined : transition,
    zIndex: isDragging ? 10 : undefined,
  };
  const grip = blockGrip ?? (
    <Tooltip label="Drag to reorder inside this group, or press Space and use the arrow keys. Arrows between topics still come first.">
      <button
        type="button"
        ref={setActivatorNodeRef}
        {...attributes}
        {...listeners}
        aria-label={`Move ${title || 'Topic'} within its group`}
        className={`${GRIP} flex-shrink-0 ${ITEM_GRIP_REVEAL}`}
      >
        <GripIcon />
      </button>
    </Tooltip>
  );
  return (
    <li
      ref={setNodeRef}
      style={style}
      className={`group/item relative rounded-md ${isDragging ? 'bg-surface-container-high ring-1 ring-primary/30' : ''}`}
    >
      <Row link={link} grip={grip} />
      {below}
    </li>
  );
};

/** A linked group's own list, sortable inside itself: its own drag context, so a row never leaves its group. */
const GroupSortable: React.FC<{
  ids: string[];
  titleOf: (id: string) => string;
  linkOf: (id: string) => React.ReactNode;
  belowOf: (id: string) => React.ReactNode;
  blockGrip?: React.ReactNode;
  onReorderGroup: (ids: string[]) => void;
}> = ({ ids, titleOf, linkOf, belowOf, blockGrip, onReorderGroup }) => {
  const sensors = useSensors(
    useSensor(PointerSensor, sensorOptions.pointer),
    useSensor(TouchSensor, sensorOptions.touch),
    useSensor(KeyboardSensor, sensorOptions.keyboard),
  );
  const name = (id: string | number) => titleOf(String(id)) || 'Topic';
  const where = (id: string | number) => `position ${ids.indexOf(String(id)) + 1} of ${ids.length} in its group`;
  const announcements: Announcements = {
    onDragStart: ({ active }) => `Picked up ${name(active.id)}, ${where(active.id)}.`,
    onDragOver: ({ active, over }) =>
      over ? `${name(active.id)} moved to ${where(over.id)}.` : `${name(active.id)} is not over a place in its group.`,
    onDragEnd: ({ active, over }) =>
      over ? `${name(active.id)} dropped at ${where(over.id)}.` : `${name(active.id)} dropped where it was.`,
    onDragCancel: ({ active }) => `Reorder cancelled. ${name(active.id)} stays where it was.`,
  };
  const onDragEnd = ({ active, over }: DragEndEvent) => {
    if (!over || active.id === over.id) return;
    const from = ids.indexOf(String(active.id));
    const to = ids.indexOf(String(over.id));
    if (from < 0 || to < 0) return;
    onReorderGroup(arrayMove(ids, from, to));
  };
  return (
    <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={onDragEnd} accessibility={{ announcements }} modifiers={VERTICAL_ONLY}>
      <SortableContext items={ids} strategy={verticalListSortingStrategy}>
        <ol className="mb-0 space-y-0.5">
          {ids.map((id, i) => (
            <SortableGroupItem
              key={id}
              id={id}
              title={titleOf(id)}
              link={linkOf(id)}
              blockGrip={i === 0 ? blockGrip : undefined}
              below={belowOf(id)}
            />
          ))}
        </ol>
      </SortableContext>
    </DndContext>
  );
};

interface BlockViewProps {
  block: Block;
  sectionsById: Map<string, TreeSection>;
  titleOf: (id: string) => string;
  isDone?: (id: string) => boolean;
  activeId: string | null;
  unread?: Record<string, number>;
  onJump: (id: string) => void;
  handle?: HandleProps;
  /** Present on a linked group whose rows may be reordered inside it: the group's new order. */
  onReorderGroup?: (ids: string[]) => void;
  /** A group row's own container, rendered beneath the row. */
  belowOf: (id: string) => React.ReactNode;
}

/** One block in the rail: a lone item, or a linked run held by a thin rule down its left edge. */
const BlockView: React.FC<BlockViewProps> = ({
  block,
  sectionsById,
  titleOf,
  isDone,
  activeId,
  unread,
  onJump,
  handle,
  onReorderGroup,
  belowOf,
}) => {
  const linkOf = (id: string) => {
    const section = sectionsById.get(id);
    if (!section) return null;
    return (
      <RailItem
        section={section}
        title={titleOf(id)}
        on={id === activeId}
        fresh={unread?.[id] ?? 0}
        done={!!isDone?.(id)}
        onJump={onJump}
      />
    );
  };
  const blockGrip = handle ? <BlockGrip handle={handle} /> : undefined;

  if (block.ids.length === 1)
    return (
      <>
        <Row link={linkOf(block.ids[0])} grip={blockGrip} />
        {belowOf(block.ids[0])}
      </>
    );
  return (
    <>
      <span aria-hidden="true" className="absolute -left-1 bottom-1.5 top-1.5 w-0.5 rounded-full bg-outline-variant" />
      {onReorderGroup ? (
        <GroupSortable
          ids={block.ids}
          titleOf={titleOf}
          linkOf={linkOf}
          belowOf={belowOf}
          blockGrip={blockGrip}
          onReorderGroup={onReorderGroup}
        />
      ) : (
        <ol className="mb-0 space-y-0.5">
          {block.ids.map((id, i) => (
            <li key={id}>
              <Row link={linkOf(id)} grip={i === 0 ? blockGrip : undefined} />
              {belowOf(id)}
            </li>
          ))}
        </ol>
      )}
    </>
  );
};

interface SortableBlockProps extends Omit<BlockViewProps, 'handle'> {
  label: string;
}

const SortableBlock: React.FC<SortableBlockProps> = ({ label, ...view }) => {
  const { attributes, listeners, setNodeRef, setActivatorNodeRef, transform, transition, isDragging } = useSortable({
    id: view.block.key,
  });
  const style: React.CSSProperties = {
    transform: CSS.Translate.toString(transform),
    transition: prefersReducedMotion() ? undefined : transition,
    zIndex: isDragging ? 10 : undefined,
  };
  return (
    <li
      ref={setNodeRef}
      style={style}
      className={`group/block relative rounded-md ${isDragging ? 'bg-surface-container-high ring-1 ring-primary/30' : ''}`}
    >
      <BlockView
        {...view}
        handle={{
          label,
          linked: view.block.ids.length > 1,
          activator: setActivatorNodeRef,
          attributes,
          listeners,
        }}
      />
    </li>
  );
};

const ROOT = '';

interface ContainerListProps {
  /** This container's children, in reading order. */
  members: TreeSection[];
  /** Container key (a group id, or ROOT) to its children. */
  kids: Map<string, TreeSection[]>;
  sectionsById: Map<string, TreeSection>;
  titleOf: (id: string) => string;
  isDone?: (id: string) => boolean;
  activeId: string | null;
  unread?: Record<string, number>;
  onJump: (id: string) => void;
  reorderable: boolean;
  onReorder?: (ids: string[]) => void;
  /** Rows after the last block, inside the same list: the root's Proposed entry. */
  trailing?: React.ReactNode;
}

/** One container as a list of blocks, sortable inside itself, with each group's own container nested under its row. */
const ContainerList: React.FC<ContainerListProps> = ({
  members,
  kids,
  sectionsById,
  titleOf,
  isDone,
  activeId,
  unread,
  onJump,
  reorderable,
  onReorder,
  trailing,
}) => {
  const blocks = toBlocks(members);
  const sortable = reorderable && blocks.length > 1;

  const sensors = useSensors(
    useSensor(PointerSensor, sensorOptions.pointer),
    useSensor(TouchSensor, sensorOptions.touch),
    useSensor(KeyboardSensor, sensorOptions.keyboard),
  );
  // A row moved inside its linked run: this container's order, as a block drop sends it.
  const reorderGroup = (b: Block) =>
    reorderable && onReorder && b.ids.length > 1
      ? (ids: string[]) => onReorder(blocks.flatMap((other) => (other.key === b.key ? ids : other.ids)))
      : undefined;

  const keys = blocks.map((b) => b.key);
  const blockOf = (id: string | number) => blocks.find((b) => b.key === String(id));
  const label = (b: Block | undefined) => {
    if (!b) return 'Topic';
    const first = titleOf(b.ids[0]) || 'Topic';
    const rest = b.ids.length - 1;
    return rest === 0 ? first : `${first} and ${rest} linked ${rest === 1 ? 'topic' : 'topics'}`;
  };

  // Without these, dnd-kit announces raw ids to a screen reader.
  const name = (id: string | number) => label(blockOf(id));
  const where = (id: string | number) => `position ${keys.indexOf(String(id)) + 1} of ${keys.length}`;
  const announcements: Announcements = {
    onDragStart: ({ active }) => `Picked up ${name(active.id)}, ${where(active.id)}.`,
    onDragOver: ({ active, over }) =>
      over ? `${name(active.id)} moved to ${where(over.id)}.` : `${name(active.id)} is not over a place in this list.`,
    onDragEnd: ({ active, over }) =>
      over ? `${name(active.id)} dropped at ${where(over.id)}.` : `${name(active.id)} dropped where it was.`,
    onDragCancel: ({ active }) => `Reorder cancelled. ${name(active.id)} stays where it was.`,
  };

  const onDragEnd = ({ active, over }: DragEndEvent) => {
    if (!over || active.id === over.id || !onReorder) return;
    const from = keys.indexOf(String(active.id));
    const to = keys.indexOf(String(over.id));
    if (from < 0 || to < 0) return;
    onReorder(arrayMove(blocks, from, to).flatMap((b) => b.ids));
  };

  const belowOf = (id: string) => {
    const inner = kids.get(id);
    if (!inner || inner.length === 0) return null;
    return (
      <div className="pl-3 pt-0.5">
        <ContainerList
          members={inner}
          kids={kids}
          sectionsById={sectionsById}
          titleOf={titleOf}
          isDone={isDone}
          activeId={activeId}
          unread={unread}
          onJump={onJump}
          reorderable={reorderable}
          onReorder={onReorder}
        />
      </div>
    );
  };

  const view = { sectionsById, titleOf, isDone, activeId, unread, onJump, belowOf };

  return sortable ? (
    // Its own drag context, so a block never leaves its container.
    <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={onDragEnd} accessibility={{ announcements }} modifiers={VERTICAL_ONLY}>
      <ol className="mb-0 space-y-0.5">
        <SortableContext items={keys} strategy={verticalListSortingStrategy}>
          {blocks.map((b) => (
            <SortableBlock key={b.key} block={b} label={label(b)} onReorderGroup={reorderGroup(b)} {...view} />
          ))}
        </SortableContext>
        {trailing}
      </ol>
    </DndContext>
  ) : (
    <ol className="mb-0 space-y-0.5">
      {blocks.map((b) => (
        <li key={b.key} className="relative">
          <BlockView block={b} onReorderGroup={reorderGroup(b)} {...view} />
        </li>
      ))}
      {trailing}
    </ol>
  );
};

interface DocContentsProps {
  /** Accepted sections in the order the page shows them. */
  sections: TreeSection[];
  titleOf: (id: string) => string;
  /** Done topics are muted in the rail; they only appear when Show done is on. */
  isDone?: (id: string) => boolean;
  proposedCount: number;
  /** Done topics in the plan, shown or not, so an all-done plan does not read as empty. */
  doneCount: number;
  activeId: string | null;
  onJump: (id: string) => void;
  onJumpProposed: () => void;
  /** The mobile disclosure's button already says "Contents". */
  hideHeading?: boolean;
  /** Node id to how much changed there since this person last looked. */
  unread?: Record<string, number>;
  /** Owner or admin on a writable plan: blocks get grips, and so do the rows inside a linked group. */
  canReorder?: boolean;
  /** One container's section ids (the root's, or one group's) in their new block order. */
  onReorder?: (ids: string[]) => void;
}

const DocContents: React.FC<DocContentsProps> = ({
  sections,
  titleOf,
  isDone,
  proposedCount,
  doneCount,
  activeId,
  onJump,
  onJumpProposed,
  hideHeading = false,
  unread,
  canReorder = false,
  onReorder,
}) => {
  const sectionsById = new Map(sections.map((s) => [s.id, s]));
  const reorderable = canReorder && !!onReorder;
  // A parent missing from the sections (mid-refetch) files its children at the root rather than losing them.
  const kids = new Map<string, TreeSection[]>();
  sections.forEach((s) => {
    const key = s.parent_id && sectionsById.has(s.parent_id) ? s.parent_id : ROOT;
    const list = kids.get(key);
    if (list) list.push(s);
    else kids.set(key, [s]);
  });
  const hasLinked =
    reorderable &&
    Array.from(kids.values()).some((members) => {
      const blocks = toBlocks(members);
      return blocks.length > 1 && blocks.some((b) => b.ids.length > 1);
    });

  const proposedItem = (
    <Appear show={proposedCount > 0} as="li" className="block">
      <a
        href={`#${PROPOSED_DOM_ID}`}
        onClick={(e) => {
          e.preventDefault();
          onJumpProposed();
        }}
        className={`${ITEM} np-state text-on-surface-variant opacity-80 hover:opacity-100`}
      >
        <span className="min-w-0 truncate">Proposed</span>
        <span className="ml-auto rounded-full bg-surface-container-highest px-1.5 text-[11px] tabular-nums">{proposedCount}</span>
      </a>
    </Appear>
  );

  return (
    <nav aria-label="Contents">
      {!hideHeading && <div className="mb-2 px-2 text-xs font-semibold text-on-surface-variant">Contents</div>}
      {sections.length === 0 && proposedCount === 0 ? (
        <EmptyState variant="line" className="px-2">{doneCount > 0 ? 'Every topic is done.' : 'No topics yet.'}</EmptyState>
      ) : (
        <ContainerList
          members={kids.get(ROOT) ?? []}
          kids={kids}
          sectionsById={sectionsById}
          titleOf={titleOf}
          isDone={isDone}
          activeId={activeId}
          unread={unread}
          onJump={onJump}
          reorderable={reorderable}
          onReorder={onReorder}
          trailing={proposedItem}
        />
      )}
      <Reveal show={!!hasLinked}>
        <p className="mt-2 px-2 pb-1 text-[11.5px] leading-snug text-on-surface-variant">Linked topics move together.</p>
      </Reveal>
    </nav>
  );
};

export default DocContents;
